import { devLog, devWarn, devError } from '@/utils/devLog';

// Debug configuration
const DEBUG_MODE = false;

// Import meta environment type
interface ImportMetaEnv {
  VITE_DEBUG_SESSION_TIMEOUT_MS?: string;
  VITE_USE_DEV_ENVIRONMENT?: string;
  VITE_FIRESTORE_DATABASE_OVERRIDE?: string;
  VITE_ANALYTICS_ENABLED?: string;
  VITE_FIREBASE_API_KEY: string;
  VITE_FIREBASE_AUTH_DOMAIN: string;
  VITE_FIREBASE_PROJECT_ID: string;
  VITE_FIREBASE_MESSAGING_SENDER_ID: string;
  VITE_FIREBASE_APP_ID: string;
  VITE_FIREBASE_MEASUREMENT_ID?: string;
  DEV: boolean;
  MODE: string;
}

// Environment toggle - controls dev vs production resources
const USE_DEV_ENVIRONMENT = import.meta.env.VITE_USE_DEV_ENVIRONMENT === 'true';
const FIRESTORE_DB_OVERRIDE = (import.meta.env.VITE_FIRESTORE_DATABASE_OVERRIDE || '').trim();
const DEV_DATABASE_ID = 'arterra-dev';

// Immediate startup log to verify env loading (dev only)
devLog(`[Firebase Config] VITE_USE_DEV_ENVIRONMENT raw value: "${import.meta.env.VITE_USE_DEV_ENVIRONMENT}"`);
devLog(`[Firebase Config] USE_DEV_ENVIRONMENT resolved to: ${USE_DEV_ENVIRONMENT}`);

interface ImportMeta {
  env: ImportMetaEnv;
}

// Session expiration timeout (default: 30 minutes)
// SECURITY: Only allow env-based override in development mode
// URL parameters and localStorage overrides have been removed to prevent
// attackers from extending sessions indefinitely
export const SESSION_TIMEOUT = (() => {
  // In production, always use fixed 30-minute timeout - not overridable
  if (!import.meta.env.DEV) {
    return 30 * 60 * 1000; // 30 minutes
  }

  // In development mode only, allow env variable override for testing
  try {
    const fromEnv = parseInt((import.meta as ImportMeta).env?.VITE_DEBUG_SESSION_TIMEOUT_MS || '', 10);
    if (Number.isFinite(fromEnv) && fromEnv > 0) {
      devLog(`[Firebase Config] Using debug session timeout: ${fromEnv}ms`);
      return fromEnv;
    }
  } catch {
    // Intentionally ignored - parsing errors fall through to default
  }

  // Default: 30 minutes
  return 30 * 60 * 1000;
})();

const debugLog = (...args: unknown[]) => {
  if (DEBUG_MODE) {
    devLog('[Firebase Config]', ...args);
  }
};

import { initializeApp } from 'firebase/app';
import { initializeAuth, getAuth, GoogleAuthProvider, signOut as firebaseSignOut, browserLocalPersistence, inMemoryPersistence, type Auth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getAnalytics, isSupported, Analytics } from 'firebase/analytics';

// Function to validate environment variables
const validateEnvVariables = () => {
  const requiredVars = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };

  // Optional variables - use undefined instead of null for Firebase compatibility
  const optionalVars = {
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || undefined,
  };

  const missingVars = Object.entries(requiredVars)
    .filter(([_, value]) => !value)
    .map(([key]) => `VITE_FIREBASE_${key.toUpperCase()}`);

  if (missingVars.length > 0) {
    throw new Error(
      `Missing required environment variables:\n${missingVars.join('\n')}\n\n` +
      `Please check your .env or .env.local file.`
    );
  }

  debugLog('Environment variables validated successfully');
  return { ...requiredVars, ...optionalVars };
};

// Get and validate environment variables
const envVars = validateEnvVariables();

// Firebase configuration
const firebaseConfig = {
  apiKey: envVars.apiKey,
  authDomain: envVars.authDomain,
  projectId: envVars.projectId,
  messagingSenderId: envVars.messagingSenderId,
  appId: envVars.appId,
  measurementId: envVars.measurementId,
};

// Initialize Firebase
let app;
try {
  app = initializeApp(firebaseConfig);
  debugLog('Firebase initialized successfully');
} catch (error) {
  devError('Error initializing Firebase:', error);
  throw error;
}

// Initialize Auth with localStorage persistence for cross-tab auth state
// Falls back to in-memory if localStorage unavailable
let auth: Auth;
try {
  auth = initializeAuth(app, { persistence: [browserLocalPersistence, inMemoryPersistence] });
  debugLog('Firebase Auth initialized with local/in-memory persistence');
} catch (e) {
  // Fallback to default instance if initializeAuth not available
  auth = getAuth(app);
  debugLog('Fallback getAuth(app) used due to initializeAuth issue', e);
}

// Check for and clear problematic persistence
const clearProblematicPersistence = async () => {
  try {
    // Check if there's a stuck auth state
    const hasStuckAuth = localStorage.getItem('firebase:host:' + firebaseConfig.authDomain);
    if (hasStuckAuth) {
      // Clear all Firebase persistence
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('firebase:') || key.includes('firebase')) {
          localStorage.removeItem(key);
        }
      });
    }
  // FIX-R6-15: Log instead of bare empty catch (CCS-3 violation)
  } catch (error) {
    devWarn('[Firebase] Persistence check error:', error);
  }
};

// Run on initialization
clearProblematicPersistence();

// Configure auth action code settings with custom URLs
// The /auth/action route will intercept Firebase redirects and send users to our custom pages
const actionCodeSettings = {
  url: window.location.origin + '/auth/action',
  handleCodeInApp: true
};

// Configure Google Auth Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('profile');
googleProvider.addScope('email');
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// Get Firestore instance - override > dev toggle > prod default
const PROD_DATABASE_ID = 'prod';
const firestoreDbId = FIRESTORE_DB_OVERRIDE || (USE_DEV_ENVIRONMENT ? DEV_DATABASE_ID : PROD_DATABASE_ID);
const db = getFirestore(app, firestoreDbId);


// Log environment status (dev only)
if (FIRESTORE_DB_OVERRIDE) {
  devWarn('╔════════════════════════════════════════════════════════════╗');
  devWarn(`║  🔬 PROD-TEST: Firestore="${FIRESTORE_DB_OVERRIDE}" | Bucket: PROD`);
  devWarn('╚════════════════════════════════════════════════════════════╝');
} else if (USE_DEV_ENVIRONMENT) {
  devWarn('╔════════════════════════════════════════════════════════════╗');
  devWarn('║  🧪 DEV ENVIRONMENT ACTIVE                                 ║');
  devWarn(`║  Firestore: ${DEV_DATABASE_ID}   ║`);
  devWarn('╚════════════════════════════════════════════════════════════╝');
} else {
  devLog(`Environment: PRODUCTION (db: ${PROD_DATABASE_ID})`);
}

// Enable detailed logging in development
if (import.meta.env.DEV && DEBUG_MODE) {
  debugLog('Firebase Environment:', import.meta.env.MODE);
  debugLog('Firebase Config:', {
    authDomain: firebaseConfig.authDomain,
    projectId: firebaseConfig.projectId,
    messagingSenderId: firebaseConfig.messagingSenderId,
    appId: firebaseConfig.appId,
    apiKeyLength: firebaseConfig.apiKey.length,
    apiKeyPrefix: firebaseConfig.apiKey.substring(0, 6) + '...'
  });
}

// Add a utility function for completely clearing Firebase auth state
export const clearFirebaseAuthState = async () => {
  try {
    const auth = getAuth();
    await firebaseSignOut(auth);

    // Clear specific Firebase local storage entries (fast, synchronous)
    Object.keys(localStorage).forEach(key => {
      if (
        key.startsWith('firebase:') ||
        key.startsWith('firebaseui:') ||
        key.startsWith('firebaseLocalStorageDb') ||
        key.includes('firebase')
      ) {
        localStorage.removeItem(key);
        debugLog(`Removed localStorage key: ${key}`);
      }
    });

    // Clear IndexedDB Firebase stores in PARALLEL (fire and forget - don't block redirect)
    // This is non-critical cleanup; the signOut above is what matters for security
    if (typeof indexedDB !== 'undefined') {
      const databases = [
        'firebaseLocalStorage',
        'firebaseLocalStorageDb',
        'firebase-installations-database',
        'firebase-heartbeat-database',
        'firebase-messaging-database'
      ];

      // Delete all databases in parallel with short timeout, don't await
      databases.forEach(dbName => {
        try {
          const req = indexedDB.deleteDatabase(dbName);
          req.onsuccess = () => debugLog(`IndexedDB '${dbName}' deleted`);
          req.onerror = () => debugLog(`IndexedDB '${dbName}' deletion error (non-critical)`);
          req.onblocked = () => debugLog(`IndexedDB '${dbName}' blocked (will be cleaned on next load)`);
        } catch (err) {
          // Non-critical, don't log as error
          debugLog(`IndexedDB '${dbName}' cleanup skipped:`, err);
        }
      });
    }

    return true;
  } catch (error) {
    devError('Error clearing Firebase auth state:', error);
    return false;
  }
};

/**
 * Options for {@link performGlobalLogout}.
 *
 * round-15 #8 (§4.3 identity safety): `validateIdentity` is an optional predicate supplied by
 * a request-lifecycle caller (apiUtils' `handleSessionExpiration`) that is closed over the
 * UID of the identity whose 401 triggered this logout. Request delivery remains guarded by
 * `{uid, generation}`, but logout validation is UID-only because logout's own cache clear bumps
 * that generation. The logout
 * routine RE-EVALUATES it immediately before each DESTRUCTIVE phase (and especially just
 * before the Firebase sign-out) and ABORTS the logout if it returns `false` — because the
 * account can switch to a DIFFERENT user during the deliberate 50ms broadcast delay (and the
 * dynamic imports) between the caller's own pre-logout identity check and the actual
 * cookie/storage/cache/Firebase teardown. Without this re-check, an unrecoverable 401 for
 * account A that lands just as the user switches to account B would tear down B's freshly
 * established session. When `validateIdentity` is omitted (a direct user-initiated logout, or
 * a legacy caller), logout proceeds unconditionally, exactly as before.
 */
export interface PerformGlobalLogoutOptions {
  /** Returns `true` while the logout-originating identity is still the current one; `false`
   * once the account has switched or a fresh same-uid session has been established. Re-checked
   * SYNCHRONOUSLY immediately before each destructive phase / cross-tab signal (round-18 CX#8):
   * a synchronous predicate cannot itself yield the event loop, so there is no window between
   * the check passing and the side effect firing. */
  validateIdentity?: () => boolean;
}

/**
 * Shared logout function that can be used across the application.
 *
 * This function performs a complete logout by:
 * 1. Clearing the token cache
 * 2. Broadcasting logout to other tabs (with 50ms propagation delay)
 * 3. Selectively clearing auth-related storage (preserving user preferences)
 * 4. Clearing cookies
 * 5. Clearing service worker caches
 * 6. Clearing Firebase auth state (signOut + non-blocking IndexedDB cleanup)
 * 7. Redirecting to login
 *
 * round-15 #8: when called from a request lifecycle, pass `{ validateIdentity }` so the
 * destructive phases abort if the account switches mid-logout (see
 * {@link PerformGlobalLogoutOptions}).
 *
 * @see /docs/plans/2025-01-11-logout-and-seamless-updates-design.md
 */
export const performGlobalLogout = async (options?: PerformGlobalLogoutOptions) => {
  const logoutStartTime = Date.now();
  devLog('[Logout] Starting global logout at', new Date().toISOString());

  // round-15 #8: re-validate the logout-originating identity before a destructive phase. A
  // `false` result means the account switched during the broadcast delay / imports since the
  // caller's own pre-logout check — we must NOT tear down the newly-current identity's session.
  // Returns `true` (proceed) when no validator was supplied (direct/legacy logout).
  const identityStillValid = (): boolean => {
    if (!options?.validateIdentity) return true;
    try {
      // SYNCHRONOUS (round-18 CX#8): no await — the check and the side effect it guards run in
      // the same tick, so the identity cannot switch between them.
      return options.validateIdentity() === true;
    } catch {
      // A validator that itself errors cannot prove the identity still matches — fail CLOSED
      // (abort the logout) rather than risk clearing a session we can't confirm is the target's.
      return false;
    }
  };

  try {
    // Track logout event before cleanup (best effort)
    try {
      const { analyticsService } = await import('@/services/analyticsService');
      const { consentService } = await import('@/services/consentService');
      const { redactSecretPath } = await import('@/utils/redactSecretPath');
      if (consentService.hasConsentFor('analytics')) {
        analyticsService.trackFeatureUsage('auth', 'logout', {
          // §4.3 (round-10 #10-FE): logging out FROM a token-bearing route (e.g.
          // `/owner/claims/verify/<token>`) would otherwise ship the raw single-use token as
          // the `page_path`. Scrub at the call site (defense-in-depth alongside the terminal
          // `sendEvent` scrub) so this bypass-AnalyticsContext caller never leaks the token.
          page_path: redactSecretPath(window.location.pathname),
        });
      }
    } catch { /* Don't block logout */ }

    // PRE-STEP: load the cache-clear helpers FIRST, so the identity gate immediately below is
    // the LAST thing before any destructive action or cross-tab signal — with NO await between
    // the gate and the synchronous side effects that follow.
    const { clearTokenCache, clearCsrfTokenCache } = await import('@/utils/apiUtils');

    // round-18 CX#8 / ADV#10 (§4.3 identity safety): EARLY synchronous gate — BEFORE the cache
    // clears, the `globalLogoutAt` storage signal, and the cross-tab broadcast. The awaits above
    // (analytics imports + this import) are the window in which the account can switch to a
    // DIFFERENT user after the caller's own pre-logout check. R-S validated only AFTER these
    // effects (Step 2 below), so an orphaned A-logout that ultimately aborts had ALREADY cleared
    // B's caches (bumping the token generation → aborting B's in-flight requests) and fired the
    // cross-tab logout signals (signing B out in every other tab). Gate here so none of that
    // runs for a switched-in identity.
    if (!identityStillValid()) {
      devLog('[Logout] Aborted before cache-clear/broadcast — identity switched (round-18 CX#8)');
      return;
    }

    // Identity confirmed to still match the logout target. Capture its uid to TAG the cross-tab
    // signals: a receiving tab currently showing a DIFFERENT account must be able to no-op a
    // logout that was not meant for it, rather than blindly signing its own live user out.
    const targetUid = auth.currentUser?.uid ?? null;

    // PRE-STEP: Clear token + CSRF caches on logout (synchronous — no await since the gate).
    clearTokenCache();
    clearCsrfTokenCache();
    devLog('[Logout] Pre-step complete: Token + CSRF caches cleared');

    // STEP 1: Broadcast logout to other tabs/windows BEFORE clearing storage
    // This ensures other tabs receive the signal before we clear anything
    try {
      // round-18 CX#8: the signal is TAGGED with the target uid (JSON payload). Legacy receivers
      // that read only presence still sign out (back-compat); uid-aware receivers no-op a
      // mismatched account. The value stays a truthy non-empty string so the storage event fires.
      localStorage.setItem('globalLogoutAt', JSON.stringify({ at: Date.now(), uid: targetUid }));
      devLog('[Logout] Step 1a: globalLogoutAt signal set in localStorage');
    } catch {
      // Intentionally ignored - localStorage may be unavailable
    }

    try {
      // Use BroadcastChannel when available for immediate propagation
      interface BroadcastChannelMessage {
        type: string;
        at: number;
        uid?: string | null;
      }
      const BroadcastChannelConstructor = (window as { BroadcastChannel?: new (name: string) => {
        postMessage: (message: BroadcastChannelMessage) => void;
        close: () => void;
      }}).BroadcastChannel;
      const bc = BroadcastChannelConstructor ? new BroadcastChannelConstructor('auth') : null;
      // round-18 CX#8: tag the broadcast with the target uid so a receiving tab on a different
      // account can no-op instead of signing its own live user out.
      bc?.postMessage({ type: 'logout', at: Date.now(), uid: targetUid });
      // Close channel shortly after to free resources
      setTimeout(() => bc?.close(), 1000);
      devLog('[Logout] Step 1b: BroadcastChannel message sent');
    } catch {
      // Intentionally ignored - BroadcastChannel may not be available
    }

    // STEP 2: Brief delay for broadcast propagation
    // Reduced from 100ms since receiving tabs now handle their own Firebase cleanup
    // via handleCrossTabLogout() - they don't depend on this tab's cleanup completing first
    await new Promise(resolve => setTimeout(resolve, 50));
    devLog('[Logout] Step 2 complete: Waited 50ms for broadcast propagation');

    // PRE-STEP: load the auth-storage clear helpers FIRST, so the identity gate immediately below
    // is the LAST thing before the first destructive action — with NO await between the gate and
    // the synchronous clearAuthStorage() that follows (mirrors the 1st-gate pattern above, where
    // the apiUtils import was hoisted before its gate). round-19 #6 (§4.3 identity safety): pre-fix
    // this `await import('@/utils/authStorage')` sat BETWEEN the gate and clearAuthStorage(), so a
    // switch during that dynamic-import window would let the continuation clear the NEW session's
    // auth storage after the gate had already passed for the old identity.
    const { clearAuthStorage, clearAllSessionStorage } = await import('@/utils/authStorage');

    // round-15 #8 / round-19 #6 (§4.3 identity safety): the 50ms broadcast delay (Step 2) and the
    // dynamic imports above are exactly the window in which the account can switch to a DIFFERENT
    // user AFTER the caller's own pre-logout identity check. Re-validate here, SYNCHRONOUSLY and
    // with NO await before the first destructive phase (Step 3 clears auth storage). If the
    // identity no longer matches, ABORT the logout entirely — do NOT clear the new identity's
    // storage/cookies/caches/session and do NOT redirect (a redirect would itself disrupt the
    // newly-current user). The orphaned A logout simply no-ops. (No validator supplied → always
    // valid → proceeds as before.)
    if (!identityStillValid()) {
      devLog('[Logout] Aborted before storage clear — identity switched during broadcast delay (round-15 #8 / round-19 #6)');
      return;
    }

    // STEP 3: Clear auth storage SELECTIVELY (not nuclear clear)
    // This preserves user preferences like dashboard layouts, theme settings, etc.
    // while clearing all authentication-related data
    const localStorageResult = clearAuthStorage();
    devLog(`[Logout] Step 3a complete: Cleared ${localStorageResult.cleared} auth keys, preserved ${localStorageResult.preserved} keys`);

    const sessionStorageCleared = clearAllSessionStorage();
    devLog(`[Logout] Step 3b complete: Cleared ${sessionStorageCleared} sessionStorage entries`);

    // STEP 4: Clear ALL site cookies (JS-accessible only)
    // HttpOnly cookies must be cleared by the backend via Set-Cookie headers
    document.cookie.split(';').forEach(cookie => {
      const [name] = cookie.trim().split('=');
      // Clear for all possible paths and domains
      const cookieBase = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT;`;
      document.cookie = `${cookieBase} path=/;`;
      document.cookie = `${cookieBase} path=/; domain=${window.location.hostname};`;
      document.cookie = `${cookieBase} path=/; domain=.${window.location.hostname};`;
    });
    devLog('[Logout] Step 4 complete: JS-accessible cookies cleared');

    // STEP 5: Clear ALL application caches (service worker)
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(async (cacheName) => {
          await caches.delete(cacheName);
        }));
        devLog(`[Logout] Step 5 complete: Cleared ${cacheNames.length} service worker caches`);
      } catch (err) {
        // Silently handle cache clearing errors
        devLog('[Logout] Step 5 complete: Cache clearing encountered non-critical error');
      }
    } else {
      devLog('[Logout] Step 5 skipped: Caches API not available');
    }

    // round-15 #8 (§4.3 identity safety): re-validate ONE MORE TIME immediately before the
    // Firebase sign-out — the single most destructive phase (it calls `signOut()` and deletes
    // the Firebase IndexedDB session). The cache/cookie clears above each `await`, so the
    // account could have switched between the Step-3 check and here. If it no longer matches,
    // ABORT before signing out — never sign out the newly-current identity. (Storage/cookies
    // cleared above are auth-scoped and will be re-established by the current session; the
    // irreversible harm this specifically prevents is tearing down B's live Firebase session.)
    if (!identityStillValid()) {
      devLog('[Logout] Aborted before Firebase sign-out — identity switched mid-logout (round-15 #8)');
      return;
    }

    // STEP 6: Clear Firebase auth state
    // This handles:
    // - Firebase signOut()
    // - IndexedDB deletion (all 5 databases)
    // - Any remaining Firebase localStorage keys (redundant with Step 3 but ensures completeness)
    await clearFirebaseAuthState();
    devLog('[Logout] Step 6 complete: Firebase auth state cleared');

    // STEP 7: Force redirect to completely fresh state
    const totalTime = Date.now() - logoutStartTime;
    devLog('[Logout] Complete logout took', totalTime, 'ms. Redirecting to login...');
    window.location.replace('/login?t=' + Date.now());

  } catch (error) {
    devError('[Logout] Error during logout:', error);
    // Force redirect even if cleanup fails - add timestamp to bypass cache
    // User should never be "stuck" logged in due to an error
    window.location.replace('/login?t=' + Date.now());
  }
};

// Analytics instance - lazily initialized only when consent is given
let analytics: Analytics | null = null;
let analyticsInitialized = false;
let analyticsInitPromise: Promise<Analytics | null> | null = null;

/**
 * Initialize Firebase Analytics lazily
 * Only called when user has granted analytics consent
 * Returns the analytics instance or null if not supported
 */
export const initializeAnalyticsLazy = async (): Promise<Analytics | null> => {
  if (import.meta.env.VITE_ANALYTICS_ENABLED === 'false') {
    devLog('[Firebase] Analytics disabled via VITE_ANALYTICS_ENABLED');
    return null;
  }

  // Auto-disable in development unless explicitly enabled
  if (import.meta.env.DEV && import.meta.env.VITE_ANALYTICS_ENABLED !== 'true') {
    devLog('[Firebase] Analytics disabled in development (set VITE_ANALYTICS_ENABLED=true to override)');
    return null;
  }

  // Return cached promise if initialization is in progress
  if (analyticsInitPromise) {
    return analyticsInitPromise;
  }

  // Return existing instance if already initialized
  if (analyticsInitialized) {
    return analytics;
  }

  analyticsInitPromise = (async () => {
    try {
      const supported = await isSupported();
      if (supported && typeof window !== 'undefined') {
        analytics = getAnalytics(app);
        analyticsInitialized = true;
        devLog('[Firebase] Analytics initialized (lazy load on consent)');
        return analytics;
      }
      devLog('[Firebase] Analytics not supported in this environment');
      return null;
    } catch (error) {
      analyticsInitPromise = null;
      devLog('[Firebase] Analytics initialization failed (will retry on next call):', error);
      return null;
    }
  })();

  return analyticsInitPromise;
};

/**
 * Get the current analytics instance
 * Returns null if not yet initialized
 */
export const getAnalyticsInstance = (): Analytics | null => analytics;

/**
 * Check if analytics has been initialized
 */
export const isAnalyticsInitialized = (): boolean => analyticsInitialized;

export { analytics };

export { auth, db, googleProvider, actionCodeSettings, USE_DEV_ENVIRONMENT };
export default app;
